import { useCallback } from 'react';
import { parseCurrency, formatToBrazilianDecimal } from '../../../utils/formatters';
import { ALL_PLANOS } from '../../../utils/constantes';

/**
 * Divide o restante (valorFinal - atoEfetivo) entre E2 e E3, mantendo o valor bruto recebido no
 * campo Ato. Compartilhado entre handleAtoChange e handleCartaDeCreditoChange: a única diferença
 * real entre os dois handlers é como o "ato efetivo" é calculado antes de chamar esta função
 * (parseCurrency(value) direto vs Math.max(cartaValue, atoMinimo)) — mantendo a regra de
 * distribuição (arredondamento, pisos '0,01'/'0') em um único lugar evita que os dois divirjam
 * silenciosamente se algum dia essa regra mudar.
 */
const distribuirRestante = (atoEfetivo, value, valorFinal) => {
  const restante = valorFinal - atoEfetivo;

  if (restante <= 0) {
    return { ato: value, e2: '0,01', e3: '0' };
  }

  const e2Value = Math.floor(restante * 100 / 2) / 100;
  const e3Value = restante - e2Value;

  return {
    ato: value,
    e2: formatToBrazilianDecimal(e2Value),
    e3: formatToBrazilianDecimal(e3Value)
  };
};

/**
 * Hook para gerenciar a atualização dos campos Ato, E2 e E3
 * 
 * REGRAS POR PLANO:
 * ┌─────────────────────┬──────────────────────────────────────────────────────┐
 * │ Plano               │ Comportamento                                        │
 * ├─────────────────────┼──────────────────────────────────────────────────────┤
 * │ À Vista             │ Redistribuição automática:                           │
 * │                     │ - Ato alterado → divide restante entre E2 e E3       │
 * │                     │ - E2 alterado → E3 recebe o restante                 │
 * │                     │ - E3 zerado → E2 recebe o restante                   │
 * ├─────────────────────┼──────────────────────────────────────────────────────┤
 * │ Fixo 12 / Fixo 18   │ Quando alterar quantidade de parcelas:               │
 * │                     │ - Recalcula Ato, E2 e E3 = valorFinal / parcelas     │
 * ├─────────────────────┼──────────────────────────────────────────────────────┤
 * │ Outros planos       │ Campos independentes, sem redistribuição             │
 * └─────────────────────┴──────────────────────────────────────────────────────┘
 * 
 * @param {Object} params - Parâmetros do hook
 * @param {Function} setFormData - Função para atualizar o formData
 * @param {number} valorFinal - Valor total (usado para redistribuição)
 * @param {string} plano - Plano selecionado
 * @param {number} minParcelas - Quantidade mínima de parcelas permitida
 * @param {number} maxParcelas - Quantidade máxima de parcelas permitida
 * @returns {Object} Handlers para mudanças nos campos
 */
export const useSignalDistribution = ({ setFormData, valorFinal = 0, plano = '', minParcelas = 0, maxParcelas = 999 }) => {
  
  const isPlanoAVista = plano === ALL_PLANOS.AVista;
  const isPlanoFixo = plano === ALL_PLANOS.Fixo12 || plano === ALL_PLANOS.Fixo18;

  /**
   * Handler para mudança no campo Ato
   * À Vista: divide o restante entre E2 e E3
   * Outros planos: apenas atualiza o campo
   */
  const handleAtoChange = useCallback((value) => {
    if (isPlanoAVista && valorFinal > 0) {
      const atoValue = parseCurrency(value) || 0;
      setFormData(prev => ({ ...prev, ...distribuirRestante(atoValue, value, valorFinal) }));
    } else {
      setFormData(prev => ({ ...prev, ato: value }));
    }
  }, [setFormData, valorFinal, isPlanoAVista]);

  /**
   * Handler para mudança no campo "Valor da Carta de Crédito"
   *
   * Usa max(valorCarta, atoMinimo) como "ato efetivo" em vez do valor bruto da Carta — replica a
   * regra do backend (GeradorPropostaFlexAVista.Simular): quando a Carta não cobre o ato mínimo,
   * a diferença vira Ato Complementar, não é absorvida por E2/E3. Vale tanto para redistribuir
   * E2/E3 no À Vista quanto para gravar em formData.ato nos demais planos (esse valor também
   * define minE2/minE3 em FlexSimulatorPage).
   *
   * @param {string} value - valor digitado no campo "Valor da Carta de Crédito"
   * @param {number} atoMinimo - ato mínimo do plano
   */
  const handleCartaDeCreditoChange = useCallback((value, atoMinimo = 0) => {
    const cartaValue = parseCurrency(value) || 0;
    const atoEfetivo = Math.max(cartaValue, atoMinimo || 0);

    if (isPlanoAVista && valorFinal > 0) {
      setFormData(prev => ({ ...prev, ...distribuirRestante(atoEfetivo, value, valorFinal) }));
    } else {
      setFormData(prev => ({ ...prev, ato: formatToBrazilianDecimal(atoEfetivo) }));
    }
  }, [setFormData, valorFinal, isPlanoAVista]);

  /**
   * Handler para mudança no campo E2
   * À Vista: ajusta E3 com o restante
   * Outros planos: apenas atualiza o campo
   */
  const handleE2Change = useCallback((value, currentAto) => {
    if (isPlanoAVista && valorFinal > 0) {
      const atoValue = parseCurrency(currentAto) || 0;
      const e2Value = parseCurrency(value) || 0;
      const restante = valorFinal - atoValue - e2Value;
      
      setFormData(prev => ({
        ...prev,
        e2: value,
        e3: restante > 0 ? formatToBrazilianDecimal(restante) : '0'
      }));
    } else {
      setFormData(prev => ({ ...prev, e2: value }));
    }
  }, [setFormData, valorFinal, isPlanoAVista]);

  /**
   * Handler para mudança no campo E3
   * À Vista: se E3 for zerado, joga o restante para E2; caso contrário, ajusta E2 para balancear
   * Outros planos: apenas atualiza o campo
   */
  const handleE3Change = useCallback((value, currentAto) => {
    if (isPlanoAVista && valorFinal > 0) {
      const e3Value = parseCurrency(value) || 0;
      
      // Se E3 for zerado, joga o restante para E2
      if (e3Value === 0) {
        const atoValue = parseCurrency(currentAto) || 0;
        const restante = valorFinal - atoValue;
        
        setFormData(prev => ({
          ...prev,
          e2: restante > 0 ? formatToBrazilianDecimal(restante) : '0',
          e3: '0'
        }));
      } else {
        // Se E3 não for zerado, ajusta E2 para manter o balanço
        const atoValue = parseCurrency(currentAto) || 0;
        const restante = valorFinal - atoValue - e3Value;
        
        setFormData(prev => ({
          ...prev,
          e2: restante > 0 ? formatToBrazilianDecimal(restante) : '0',
          e3: value
        }));
      }
    } else {
      setFormData(prev => ({ ...prev, e3: value }));
    }
  }, [setFormData, valorFinal, isPlanoAVista]);

  /**
   * Handler para mudança na quantidade de parcelas
   * Fixo 12/18: recalcula Ato, E2 e E3 = valorFinal / novaParcela
   * Outros planos: apenas atualiza o campo
   * Aplica apenas o limite máximo durante a digitação
   */
  const handleQuantidadeParcelasChange = useCallback((value) => {
    let novaParcelas = Number.parseInt(value) || 0;
    
    // Aplica apenas o limite máximo durante a digitação
    // O limite mínimo será aplicado no onBlur
    if (novaParcelas > maxParcelas) {
      novaParcelas = maxParcelas;
    }
    
    if (isPlanoFixo && valorFinal > 0 && novaParcelas > 0) {
      // Recalcula o valor por parcela e distribui para Ato, E2 e E3
      const valorPorParcela = valorFinal / novaParcelas;
      
      console.log('[handleQuantidadeParcelasChange] Recalculando parcelas:', {
        valorFinal,
        novaParcelas,
        valorPorParcela
      });
      
      setFormData(prev => ({
        ...prev,
        quantidadeParcelas: novaParcelas,
        ato: formatToBrazilianDecimal(valorPorParcela),
        e2: formatToBrazilianDecimal(valorPorParcela),
        e3: formatToBrazilianDecimal(valorPorParcela)
      }));
    } else {
      // Para outros planos, apenas atualiza a quantidade
      setFormData(prev => ({ ...prev, quantidadeParcelas: novaParcelas }));
    }
  }, [setFormData, valorFinal, isPlanoFixo, maxParcelas]);

  /**
   * Handler para quando o campo perde o foco (onBlur)
   * Aplica o limite mínimo se necessário
   */
  const handleQuantidadeParcelasBlur = useCallback(() => {
    setFormData(prev => {
      const parcelasAtual = Number.parseInt(prev.quantidadeParcelas) || 0;
      
      // Se estiver abaixo do mínimo, ajusta para o mínimo
      if (parcelasAtual > 0 && parcelasAtual < minParcelas) {
        return { ...prev, quantidadeParcelas: minParcelas };
      }
      
      return prev;
    });
  }, [setFormData, minParcelas]);

  return {
    handleAtoChange,
    handleCartaDeCreditoChange,
    handleE2Change,
    handleE3Change,
    handleQuantidadeParcelasChange,
    handleQuantidadeParcelasBlur
  };
};
